import Cookies from "js-cookie";
import { timeToMiliseconds } from "@src/helpers/timeUtils";
import { removeValueFromLocalStorage } from "@src/helpers/webStorage";
import { isAndroid, isIos } from "@src/helpers/deviceDetection";
import { getWelcomeMessage } from "asos-site-chrome-store-presentation";
import { isWindowDefined } from "@src/helpers/url";

export const OPEN_IN_APP_DEFAULT_ACTION = 0;
export const OPEN_IN_APP_USER_ACTION = 1;
export const OPEN_IN_APP_UNEXPECTED_USER_ACTION = 2;

const appPreferencesDefaults = {
  openInApp: OPEN_IN_APP_DEFAULT_ACTION,
  expireOpenInApp: 0,
  expiresOn: 0,
  appIsPrimaryChannel: false
};

const appsUrlsMap = {
  genericFloor: "asos://home?",
  womenFloor: "asos://home?floor=women",
  menFloor: "asos://home?floor=men",
  product: "asos://product?iid="
};

const MEN_PATH_REGEX = [
    /\/men(\/)?$/,
    /\/uomo(\/)?$/,
    /\/mezczyzni(\/)?$/,
    /\/maend(\/)?$/,
    /\/hombre(\/)?$/,
    /\/herren(\/)?$/,
    /\/homme(\/)?$/,
    /\/man(\/)?$/,
    /\/heren(\/)?$/
  ],
  WOMEN_PATH_REGEX = [
    /\/women(\/)?$/,
    /\/donna(\/)?$/,
    /\/kobiety(\/)?$/,
    /\/kvinder(\/)?$/,
    /\/mujer(\/)?$/,
    /\/damen(\/)?$/,
    /\/femme(\/)?$/,
    /\/kvinna(\/)?$/,
    /\/dames(\/)?$/
  ];

/* istanbul ignore next */
export const isWindowHidden = () => {
  return document.visibilityState == "hidden";
};

export const ANDROID_APP_STORE_URL =
  "https://c00.adobe.com/b5c2c04c-c0b3-4dbe-b038-e1f9e2c2c0eb/xn6rdi48/g/com.asos.app";
export const APPLE_APP_STORE_URL =
  "https://c00.adobe.com/v2/gb/f8077542-cbcf-49fe-9d22-48f32990573d/xfv9thai/i/457876088";
export const TIMEOUT_OPEN_IN_APP = 3000;

export const get = key => {
  /* istanbul ignore next */
  if (!isWindowDefined()) {
    return;
  }
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    return localStorage.getItem(key);
  }
};

export const set = (key, value) => {
  localStorage.setItem(
    key,
    typeof value === "object" ? JSON.stringify(value) : value
  );
};

const getTimeToRememberOpenInAppAction = timeToMiliseconds(0, 72, 0, 0); // 259200000; // 72 hours
const getTimeToRememberUserDiscardedDialog = timeToMiliseconds(30, 0, 0, 0); // 2592000000; // 30 days

export const getDefaultPreferences = () => {
  return Object.assign({}, appPreferencesDefaults);
};

export const getDiscardPreferences = () => {
  const expiresOn = Date.now() + getTimeToRememberUserDiscardedDialog;
  return Object.assign({}, appPreferencesDefaults, {
    expiresOn: expiresOn
  });
};

export const getOpenAppPreferences = () => {
  return Object.assign({}, appPreferencesDefaults, {
    openInApp: OPEN_IN_APP_USER_ACTION,
    expireOpenInApp: Date.now() + TIMEOUT_OPEN_IN_APP
  });
};

export const getOpenAppSuccesPreferences = () => {
  const toExpire = Date.now() + getTimeToRememberOpenInAppAction;
  return {
    appIsPrimaryChannel: true,
    expiresOn: toExpire,
    openInApp: OPEN_IN_APP_DEFAULT_ACTION,
    expireOpenInApp: 0
  };
};

export const initialise = (showBanner, preferences, url, pathAcceptsBanner) => {
  const now = Date.now(),
    { expiresOn, appIsPrimaryChannel } = preferences,
    isDeepLinkExpired = expiresOn <= now;

  if (appIsPrimaryChannel) {
    if (pathAcceptsBanner) {
      if (isDeepLinkExpired) {
        showBanner();
      }
      if (!isDeepLinkExpired) {
        if (isIos()) {
          showBanner();
        }
        if (isAndroid()) {
          window.location.assign(url);
        }
      }
    }
  }
  if (!appIsPrimaryChannel) {
    const hasHideBannerExpired = expiresOn <= now;
    if (hasHideBannerExpired) {
      removeValueFromLocalStorage("asos.siteChrome.nativeBanner");
      showBanner(getDefaultPreferences());
    }
  }
};

export const getAppStoreUrl = () => {
  if (isAndroid()) {
    return ANDROID_APP_STORE_URL;
  } else if (isIos()) {
    return APPLE_APP_STORE_URL;
  }
  return "";
};

export const detectPlatform = () => {
  if (isIos()) {
    return "ios";
  }
  if (isAndroid()) {
    return "android";
  }
};

export const doesPathAcceptsBanner = storeCode => {
  let path = window.location.pathname.toLowerCase();
  // remove trailing slash
  path = path.replace(/\/$/, "");

  const parsedPath = path.split("/").join(""),
    isMenPage = matchPathInUrl(path, MEN_PATH_REGEX),
    isWomenPage = matchPathInUrl(path, WOMEN_PATH_REGEX),
    isBasePath = parsedPath === storeCode.toLowerCase() || parsedPath === "",
    isPDP = window.asos.pdp != null;

  return isMenPage || isWomenPage || isBasePath || isPDP;
};

export const matchPathInUrl = (url, localisedPaths) => {
  return localisedPaths.reduce((acc, el) => {
    return acc ? true : url.search(el) > -1;
  }, false);
};

export const getCorrespondingAppUrl = (isSupportedPlatform, storeCode) => {
  if (!isSupportedPlatform || storeCode == null) return null;

  let path = window.location.pathname.toLowerCase(),
    appUrl = "";
  // remove trailing slash
  path = path.replace(/\/$/, "");
  const parsedPath = path.split("/").join(""),
    isMenPage = matchPathInUrl(path, MEN_PATH_REGEX),
    isWomenPage = matchPathInUrl(path, WOMEN_PATH_REGEX),
    isBasePath = parsedPath === storeCode.toLowerCase() || parsedPath === "";

  if (isBasePath) {
    appUrl = appsUrlsMap.genericFloor;
  } else if (isWomenPage) {
    appUrl = appsUrlsMap.womenFloor;
  } else if (isMenPage) {
    appUrl = appsUrlsMap.menFloor;
  } else if (window?.asos?.pdp) {
    const iidValue = window.asos.pdp.config.product.id;
    appUrl = appsUrlsMap.product + iidValue;
  } else {
    return null;
  }

  // check for Affiliation and other web session info to pass to the app
  const queryDelimiter = "&";
  const urlEndsWithDelimiter =
    appUrl.substr(-1) === "?" || appUrl.substr(-1) === "&";
  if (!urlEndsWithDelimiter) {
    appUrl += queryDelimiter;
  }
  appUrl += "channelref=smartbanner";

  const affiliateQueryParams = [
    "uid",
    "transaction_id",
    "awc",
    "MID",
    "siteId",
    "datetime",
    "affid",
    "pubref",
    "gclid",
    "ppcadref",
    "btn_ref",
    "_cclid",
    "clickid"
  ];

  affiliateQueryParams.forEach(function(paramName) {
    const paramValue = getUrlQueryParamValue(paramName);
    if (paramValue) {
      appUrl += "&" + paramName + "=" + paramValue;
    }
  });

  const ctaRefVal = getUrlQueryParamValue("ctaRef");
  if (ctaRefVal) {
    appUrl += "&ctaref=" + ctaRefVal;
  }

  const asosCookie = Cookies.get("asos");
  if (asosCookie) {
    let customerId = getUrlQueryParamValue("customerid", asosCookie);
    if (customerId) {
      customerId = customerId.replace("customerid=", "");
      appUrl += "&custref=" + customerId;
    }
  }

  return appUrl;
};

export const getUrlQueryParamValue = (name, cookie) => {
  let queries = cookie;
  if (cookie == null) {
    queries = window.location.search;
  }
  const params = queries.replace("?", "").split("&");
  let paramValue = "";
  for (let i = 0; i < params.length; i++) {
    if (
      params[i].length &&
      params[i].toLowerCase().indexOf(name.toLowerCase() + "=") >= 0
    ) {
      paramValue = params[i].replace(new RegExp(name + "=", "i"), "");
    }
  }
  return paramValue;
};

export const validate = (isSupportedPlatform, url, withNewProps) => {
  const welcomeMessage = withNewProps?.welcomeMessage;
  if (!isSupportedPlatform) {
    return;
  }
  if (url == null) {
    return;
  }
  const hasWelcomeMessageBeenClosed = getWelcomeMessage();
  if (hasWelcomeMessageBeenClosed) {
    return true;
  }
  if (
    welcomeMessage == null ||
    (welcomeMessage != null && Object.keys(welcomeMessage).length === 0)
  ) {
    return;
  }
  if (
    welcomeMessage.redirectUrl != null ||
    welcomeMessage.browsingLocalStore === false
  ) {
    return;
  }
  return true;
};
